import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ArrowLeft, Bot, Check, Loader2 } from 'lucide-react'

const steps = [
  { id: 'productName', label: 'Product Name', placeholder: 'What is your product or project called?', icon: '💡' },
  { id: 'mission', label: 'Mission', placeholder: 'Why does your product exist? What problem does it solve?', icon: '🎯' },
  { id: 'vision', label: 'Vision', placeholder: 'What does success look like in 5 years?', icon: '🔭' },
  { id: 'market', label: 'Target Market', placeholder: 'Who are your ideal customers?', icon: '🌍' },
  { id: 'coreNeed', label: 'Core Need', placeholder: 'What is the one thing your customers need most?', icon: '💎' },
  { id: 'solutionPrinciple', label: 'Solution Principle', placeholder: 'How do you solve this need differently?', icon: '⚡' },
  { id: 'product', label: 'Product Description', placeholder: 'Describe your product in one sentence', icon: '📦' },
  { id: 'technology', label: 'Technology Stack', placeholder: 'What tech powers your solution?', icon: '🛠️' },
]

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(0)
  const [decisions, setDecisions] = useState<Record<string, string>>({})
  const [botToken, setBotToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [deployed, setDeployed] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const progress = ((currentStep) / (steps.length + 1)) * 100

  const handleNext = () => {
    // Save current step data before moving
    if (currentStep < steps.length) {
      const stepId = steps[currentStep].id
      const textarea = document.getElementById(stepId) as HTMLTextAreaElement
      if (textarea) {
        setDecisions(prev => ({ ...prev, [stepId]: textarea.value }))
      }
    }
    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleDeploy = async () => {
    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const payload: any = { planId: 'agent', decisions }
      if (botToken) payload.botToken = botToken

      const res = await fetch('/api/agents/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json()

      if (data.success) {
        setDeployed(true)
      } else {
        setError(data.error || 'Deploy failed')
      }
    } catch {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  if (deployed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-success" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Agent Deployed!</h2>
          <p className="text-text-muted mb-8">
            Your AI agent is being configured with your 8 decisions.
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-primary text-dark font-bold rounded-xl hover:opacity-90 transition inline-flex items-center gap-2"
          >
            Go to Dashboard
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <div className="h-1 bg-glass rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-center text-text-muted text-sm mt-2">
            Step {currentStep + 1} of {steps.length + 1}
          </p>
        </div>

        {currentStep < steps.length ? (
          /* Question Step */
          <div className="p-8 rounded-2xl bg-glass border border-glass-border">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">{steps[currentStep].icon}</span>
              <div>
                <span className="text-sm text-primary font-semibold">Question {currentStep + 1}</span>
                <h2 className="text-2xl font-bold text-white">{steps[currentStep].label}</h2>
              </div>
            </div>
            
            <textarea
              value={decisions[steps[currentStep].id] || ''}
              onChange={e => setDecisions(prev => ({ ...prev, [steps[currentStep].id]: e.target.value }))}
              placeholder={steps[currentStep].placeholder}
              rows={4}
              className="w-full px-4 py-3 bg-darker border border-glass-border rounded-xl text-white focus:border-primary focus:outline-none transition resize-none"
            />
            
            <div className="flex justify-between mt-6">
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className="px-6 py-2 border border-glass-border text-white rounded-xl hover:bg-glass transition disabled:opacity-30 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-primary text-dark font-semibold rounded-xl hover:opacity-90 transition flex items-center gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          /* Deploy Step */
          <div className="p-8 rounded-2xl bg-glass border border-glass-border">
            <div className="flex items-center gap-3 mb-6">
              <Bot className="w-8 h-8 text-primary" />
              <h2 className="text-2xl font-bold text-white">Deploy Your Agent</h2>
            </div>
            
            <p className="text-text-muted mb-6">
              Review your decisions and optionally connect a Telegram bot.
            </p>
            
            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
              {steps.map(step => (
                <div key={step.id} className="p-3 rounded-lg bg-darker border border-glass-border">
                  <span className="text-sm text-primary font-semibold">{step.label}</span>
                  <p className="text-sm text-text-muted truncate">
                    {decisions[step.id] || 'Not set'}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-text-muted mb-2">
                🤖 Telegram Bot Token (Optional)
              </label>
              <input
                type="text"
                value={botToken}
                onChange={e => setBotToken(e.target.value)}
                placeholder="123456:ABC-DEF..."
                className="w-full px-4 py-3 bg-darker border border-glass-border rounded-xl text-white focus:border-primary focus:outline-none transition font-mono text-sm"
              />
              <p className="text-xs text-text-muted mt-2">
                Get it from <a href="https://t.me/BotFather" target="_blank" className="text-primary hover:underline">@BotFather</a> — or leave empty
              </p>
            </div>
            
            {error && (
              <div className="p-4 rounded-xl bg-accent/10 border border-accent/30 text-accent text-sm mb-4">
                {error}
              </div>
            )}
            
            <div className="flex justify-between">
              <button
                onClick={handleBack}
                className="px-6 py-2 border border-glass-border text-white rounded-xl hover:bg-glass transition flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleDeploy}
                disabled={loading}
                className="px-8 py-3 bg-gold text-dark font-bold rounded-xl hover:opacity-90 transition flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bot className="w-5 h-5" />}
                {loading ? 'Deploying...' : 'Deploy Agent'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
